import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Button } from '../../components/ui/button';
import {
    Wrench, ShieldCheck, CircleParking, Recycle, Building2, ConciergeBell, Car,
    Megaphone, Info, Settings2, Bell, BellOff, Circle, Plus, X,
    DollarSign, MessageSquare, Gauge, Tag, CalendarDays, Truck, Repeat, Landmark,
} from 'lucide-react';
import { BroomIcon } from '../hotelAssistant/AssistantScreens';

const ICONS = {
    wrench: Wrench, shield: ShieldCheck, parking: CircleParking, recycle: Recycle,
    building: Building2, 'concierge-bell': ConciergeBell, car: Car, broom: BroomIcon,
    megaphone: Megaphone, info: Info, circle: Circle,
    dollar: DollarSign, message: MessageSquare, gauge: Gauge, tag: Tag,
    calendar: CalendarDays, truck: Truck, repeat: Repeat, landmark: Landmark,
};

// One dot per department — GREEN clear · RED needs attention · YELLOW being handled.
const DOT = {
    green: 'bg-emerald-500',
    yellow: 'bg-amber-400',
    red: 'bg-red-500',
};
const TILE_RING = {
    // Quiet by default: flat surface, hairline border. Only a tile that needs
    // attention (or is being handled) gets the highlighted border.
    green: 'border-transparent bg-slate-100/80 dark:bg-slate-900/40 hover:border-slate-300 dark:hover:border-slate-700',
    yellow: 'border-amber-500/60 bg-amber-50 dark:bg-amber-400/5 hover:border-amber-400',
    red: 'border-red-500/70 bg-red-50 dark:bg-red-500/5 hover:border-red-400',
};

export function DepartmentStatusBar({ departments, vertical, activeDept, onOpenDepartment }) {
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || 'en').split('-')[0];
    const [prefs, setPrefs] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const byId = Object.fromEntries((departments || []).map((d) => [d.id, d]));

    const loadPrefs = useCallback(() => {
        api.get('/command-center/preferences', { params: { vertical } })
            .then((r) => setPrefs(r.data))
            .catch(() => setPrefs(null));
    }, [vertical]);

    useEffect(() => { loadPrefs(); }, [loadPrefs]);

    const save = async (next) => {
        setSaving(true);
        try {
            const r = await api.put('/command-center/preferences', {
                vertical,
                enabled_departments: next.enabled_departments,
                notifications_on: next.notifications_on,
            });
            setPrefs(r.data);
        } catch (e) {
            toast.error(t('commandCenter.act.failed'));
        } finally {
            setSaving(false);
        }
    };

    const toggleDept = (id) => {
        if (!prefs) return;
        const set = new Set(prefs.enabled_departments || []);
        set.has(id) ? set.delete(id) : set.add(id);
        const ordered = (prefs.available || []).filter((d) => set.has(d));
        save({ ...prefs, enabled_departments: ordered });
    };

    const setNotifications = (on) => {
        if (!prefs) return;
        save({ ...prefs, notifications_on: on });
    };

    const label = (id) => {
        const d = byId[id] || (prefs?.catalog || []).find((c) => c.id === id);
        return (d?.label && (d.label[lang] || d.label.en)) || id;
    };
    const iconFor = (id) => {
        const meta = byId[id] || (prefs?.catalog || []).find((c) => c.id === id);
        const Ic = ICONS[meta?.icon] || Circle;
        return <Ic className="w-4 h-4 text-slate-400" strokeWidth={1.75} />;
    };
    const enabledList = (prefs?.available || []).filter((id) => (prefs?.enabled_departments || []).includes(id));
    const availableToAdd = (prefs?.available || []).filter((id) => !enabledList.includes(id));

    const CustomizeButton = (
        <Popover onOpenChange={(o) => { if (!o) setShowAdd(false); }}>
            <PopoverTrigger asChild>
                <button
                    data-testid="cc-dept-customize"
                    className="flex flex-1 flex-col items-center justify-center gap-1.5 rounded-2xl border border-dashed border-slate-300 bg-slate-50/70 dark:border-slate-600/70 dark:bg-slate-900/30 px-3 py-3 min-w-[92px] text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:border-slate-400 dark:hover:border-slate-400/70 transition-colors">
                    <Settings2 className="w-6 h-6" />
                    <span className="text-xs font-medium">{t('commandCenter.departments.customize')}</span>
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 bg-slate-900 border-slate-700 p-0" data-testid="cc-dept-customize-popover">
                <div className="px-4 py-3 border-b border-slate-700">
                    <p className="text-sm font-semibold text-white">{t('commandCenter.departments.customizeTitle')}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{t('commandCenter.departments.customizeHint')}</p>
                </div>
                <div className="py-1 max-h-80 overflow-y-auto">
                    <p className="px-4 pt-2 pb-1 text-[11px] uppercase tracking-wide text-slate-500">{t('commandCenter.departments.displayed')}</p>
                    {enabledList.length === 0 && (
                        <p className="px-4 py-2 text-xs text-slate-500">{t('commandCenter.departments.noneDisplayed')}</p>
                    )}
                    {enabledList.map((id) => (
                        <div key={id} data-testid={`cc-dept-shown-${id}`}
                            className="flex items-center justify-between gap-3 px-4 py-2 hover:bg-slate-800/40">
                            <span className="flex items-center gap-2 text-sm text-slate-200">{iconFor(id)}{label(id)}</span>
                            <button data-testid={`cc-dept-remove-${id}`} onClick={() => toggleDept(id)} disabled={saving}
                                title={t('commandCenter.departments.remove')}
                                className="text-slate-500 hover:text-red-400 p-1 rounded">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                    {availableToAdd.length > 0 && (
                        <>
                            <button data-testid="cc-dept-add-toggle" onClick={() => setShowAdd((v) => !v)}
                                className="flex items-center gap-2 px-4 py-2.5 mt-1 w-full text-left text-sm font-medium text-emerald-400 hover:text-emerald-300 border-t border-slate-800">
                                <Plus className="w-4 h-4" />{t('commandCenter.departments.addDepartment')}
                            </button>
                            {showAdd && availableToAdd.map((id) => (
                                <button key={id} data-testid={`cc-dept-add-${id}`} onClick={() => toggleDept(id)} disabled={saving}
                                    className="flex items-center justify-between gap-3 px-6 py-2 w-full text-left hover:bg-slate-800/50">
                                    <span className="flex items-center gap-2 text-sm text-slate-300">{iconFor(id)}{label(id)}</span>
                                    <Plus className="w-4 h-4 text-emerald-400" />
                                </button>
                            ))}
                        </>
                    )}
                </div>
                <div className="px-4 py-3 border-t border-slate-700">
                    <div className="flex items-center justify-between">
                        <div className="min-w-0 pr-3">
                            <p className="text-sm text-slate-200">{t('commandCenter.departments.notifications')}</p>
                            <p className="text-[11px] text-slate-500">{t('commandCenter.departments.notificationsHint')}</p>
                        </div>
                        <button
                            data-testid="cc-dept-notifications"
                            onClick={() => setNotifications(!prefs?.notifications_on)}
                            disabled={saving}
                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${prefs?.notifications_on ? 'bg-emerald-600' : 'bg-slate-600'}`}>
                            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${prefs?.notifications_on ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );

    // Notifications OFF → collapsed strip (functionality untouched; just hidden).
    if (prefs && !prefs.notifications_on) {
        return (
            <div data-testid="cc-department-bar-off"
                className="flex items-center justify-between gap-3 rounded-2xl border border-slate-700/60 bg-slate-900/40 px-4 py-3">
                <div className="flex items-center gap-2.5 text-slate-400 min-w-0">
                    <BellOff className="w-4 h-4 shrink-0" />
                    <div className="min-w-0">
                        <p className="text-sm text-slate-300 truncate">{t('commandCenter.departments.statusOff')}</p>
                        <p className="text-[11px] text-slate-500 truncate">{t('commandCenter.departments.statusOffHint')}</p>
                    </div>
                </div>
                <Button size="sm" variant="outline" data-testid="cc-dept-turn-on"
                    onClick={() => setNotifications(true)} disabled={saving}
                    className="gap-1.5 border-slate-600 bg-slate-900/60 shrink-0">
                    <Bell className="w-3.5 h-3.5" />{t('commandCenter.departments.on')}
                </Button>
            </div>
        );
    }

    // CANONICAL ORDER: always render in the backend catalog order
    // (Maintenance → Security → Parking → Waste & Recycling → Amenities →
    // Building → …). Never let stored/DB/alphabetical order decide the visual
    // order. Icon↔title stay bound by department id (single backend source).
    const canonicalOrder = prefs?.available || (departments || []).map((d) => d.id);
    const enabledSet = new Set(prefs
        ? (prefs.enabled_departments || [])
        : (departments || []).map((d) => d.id));
    const enabledIds = canonicalOrder.filter((id) => enabledSet.has(id));
    const tiles = enabledIds.map((id) => byId[id]).filter(Boolean);

    return (
        <div data-testid="cc-department-bar">
            <div className="flex items-center gap-2 mb-3">
                <span className="text-xs uppercase tracking-[0.2em] text-slate-600">{t('commandCenter.departments.title')}</span>
                <span className="flex items-center gap-3 text-[11px] text-slate-600">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />{t('commandCenter.departments.legendGreen')}</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />{t('commandCenter.departments.legendRed')}</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" />{t('commandCenter.departments.legendYellow')}</span>
                </span>
            </div>
            <div className="flex flex-wrap gap-2.5">
                {tiles.map((d) => {
                    const Icon = ICONS[d.icon] || Circle;
                    const active = activeDept === d.id;
                    const count = d.status === 'red' ? d.new : (d.status === 'yellow' ? d.in_progress : 0);
                    return (
                        <button
                            key={d.id}
                            data-testid={`cc-dept-tile-${d.id}`}
                            onClick={() => onOpenDepartment(d.id)}
                            title={d.status === 'red' ? t('commandCenter.departments.needsAttention')
                                : d.status === 'yellow' ? t('commandCenter.departments.beingHandled')
                                : t('commandCenter.departments.allClear')}
                            className={`relative flex flex-1 flex-col items-center justify-center gap-1.5 rounded-2xl border px-3 py-3 min-w-[92px] transition-all duration-200 hover:-translate-y-0.5 ${TILE_RING[d.status]} ${active ? 'ring-2 ring-emerald-400/70' : ''}`}>
                            {count > 0 && (
                                <span data-testid={`cc-dept-count-${d.id}`}
                                    className={`absolute top-1.5 right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full text-[10px] font-bold text-white ${d.status === 'red' ? 'bg-red-500' : 'bg-amber-500'}`}>
                                    {count}
                                </span>
                            )}
                            {/* px-4 keeps a long label (F&I / Insurance) from
                                running under the count badge in the corner. */}
                            <span className="text-xs font-medium text-slate-700 dark:text-slate-100 text-center leading-tight px-4">{label(d.id)}</span>
                            <Icon className={`w-6 h-6 ${d.status === 'red' ? 'text-red-500 dark:text-red-300' : d.status === 'yellow' ? 'text-amber-600 dark:text-amber-300' : 'text-slate-500 dark:text-slate-400'}`} strokeWidth={1.75} />
                            <span data-testid={`cc-dept-dot-${d.id}`} data-status={d.status}
                                className={`w-2.5 h-2.5 rounded-full ${DOT[d.status]} ${d.status === 'red' ? 'animate-pulse' : ''}`} />
                        </button>
                    );
                })}
                {CustomizeButton}
            </div>
        </div>
    );
}
